import { useEffect } from 'react';
import { Bookmark } from 'lucide-react';
import { usePinsStore } from '@/stores/pinsStore';
import type { PinKind } from '@/services/api';
import { cn } from '@/lib/utils';

interface PinToggleProps {
  kind: PinKind;
  entityId: string;
  className?: string;
  title?: string;
}

export function PinToggle({ kind, entityId, className, title }: PinToggleProps) {
  const isLoaded = usePinsStore((s) => s.isLoaded);
  const isLoading = usePinsStore((s) => s.isLoading);
  const load = usePinsStore((s) => s.load);
  const add = usePinsStore((s) => s.add);
  const remove = usePinsStore((s) => s.remove);
  const pinned = usePinsStore((s) => s.isPinned(kind, entityId));

  useEffect(() => {
    if (!isLoaded && !isLoading) {
      void load();
    }
  }, [isLoaded, isLoading, load]);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (pinned) {
      void remove(kind, entityId);
    } else {
      void add(kind, entityId);
    }
  };

  return (
    <button
      type="button"
      onClick={handleToggle}
      title={title ?? (pinned ? 'Unpin' : 'Pin')}
      aria-pressed={pinned}
      className={cn(
        'inline-flex h-7 w-7 items-center justify-center rounded-md transition-colors',
        pinned
          ? 'text-primary-600 hover:bg-surface-100 dark:text-primary-300 dark:hover:bg-surface-800'
          : 'text-surface-300 opacity-0 hover:bg-surface-100 hover:text-surface-600 group-hover:opacity-100 dark:text-surface-600 dark:hover:bg-surface-800 dark:hover:text-surface-300',
        className
      )}
    >
      <Bookmark className={cn('h-4 w-4', pinned && 'fill-current')} />
    </button>
  );
}
